import React, { useState, useEffect, useMemo } from "react";
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Lock,
  Info,
  Check,
} from "lucide-react";
//@ts-ignore
import { tw } from "@careinfo/tailwind";
//@ts-ignore
import HTTPSnippet from "httpsnippet";

import { Buffer as BufferPolyfill } from "buffer";

if (typeof window !== "undefined") {
  // @ts-ignore
  window.Buffer = window.Buffer || BufferPolyfill;
  // @ts-ignore
  window.process = window.process || { env: {} };
}

interface EndpointItem {
  path: string;
  method: string;
  details: any;
}

interface LanguageOption {
  name: string;
  target: string;
  client: string;
}

const languageOptions: LanguageOption[] = [
  { name: "Shell", target: "shell", client: "curl" },
  { name: "Node.js", target: "node", client: "axios" },
  { name: "JavaScript", target: "javascript", client: "fetch" },
  { name: "Python", target: "python", client: "requests" },
  { name: "Go", target: "go", client: "native" },
  { name: "Java", target: "java", client: "okhttp" },
  { name: "C#", target: "csharp", client: "restsharp" },
  { name: "PHP", target: "php", client: "guzzle" },
];

const ApiDocsInterface = (props: any) => {
  const { apiData } = props;

  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [apiKey, setApiKey] = useState("key");

  const [activeLanguage, setActiveLanguage] = useState(languageOptions[0]);
  const [generatedCode, setGeneratedCode] = useState("");
  const [copied, setCopied] = useState(false);

  const baseUrl = useMemo(() => {
    let url = apiData?.host || apiData?.servers?.[0]?.url || "";
    if (url && !url.startsWith("http")) {
      url = `https://${url}`;
    }
    return url.endsWith("/") ? url.slice(0, -1) : url;
  }, [apiData]);

  const groupedMenu = useMemo(() => {
    if (!apiData?.paths) return {};
    const groups: Record<string, EndpointItem[]> = {};

    for (const path in apiData.paths) {
      const methods = apiData.paths[path];
      for (const method in methods) {
        if (method === "parameters") continue;
        const details = methods[method];
        const tag = details.tags?.[0];
        groups[tag] ||= [];
        groups[tag].push({
          path: path.startsWith("/") ? path : `/${path}`,
          method: method.toUpperCase(),
          details,
        });
      }
    }
    return groups;
  }, [apiData]);

  useEffect(() => {
    const tags = Object.keys(groupedMenu);
    if (tags.length > 0 && !selectedEndpoint) {
      const firstTag = tags[0];
      setSelectedEndpoint(groupedMenu[firstTag][0]);
      setExpandedSections({ [firstTag]: true });
    }
  }, [groupedMenu]);

  // Snippet Generation
  useEffect(() => {
    if (!selectedEndpoint || !baseUrl) return;

    const generateCodeSnippet = async () => {
      try {
        const fullUrl = `${baseUrl}${selectedEndpoint.path}`;

        const harRequest = {
          method: selectedEndpoint.method,
          url: fullUrl,
          httpVersion: "HTTP/1.1",
          queryString: [],
          headers: [
            { name: "Authorization", value: `Bearer ${apiKey}` },
            { name: "Content-Type", value: "application/json" },
          ],
          cookies: [],
          postData: {
            mimeType: "application/json",
            text: JSON.stringify({}),
          },
          headersSize: -1,
          bodySize: -1,
        };

        const snippet = new (HTTPSnippet as any)(harRequest);

        const output = await snippet.convert(
          activeLanguage.target,
          activeLanguage.client,
        );

        setGeneratedCode(
          Array.isArray(output) ? output.join("\n") : output || "",
        );
      } catch (error) {
        setGeneratedCode("// Error generating snippet");
      }
    };

    generateCodeSnippet();
  }, [selectedEndpoint, activeLanguage, apiKey, baseUrl]);

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getMethodStyles = (method: string) => {
    const httpMethod = method.toUpperCase();
    switch (httpMethod) {
      case "GET":
        return "bg-green-100 text-green-700 border-green-200";
      case "POST":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const toggleSection = (tag: string) => {
    setExpandedSections((prev) => ({ ...prev, [tag]: !prev[tag] }));
  };

  const getBodyProperties = (param: any) => {
    if (param?.in === "body" && param?.schema) {
      const ref = param.schema["$ref"];
      if (ref) {
        const definitionName = ref.split("/").pop();
        return apiData?.definitions?.[definitionName]?.properties || {};
      }
      return param.schema.properties || {};
    }
    return null;
  };

  const responseExample = useMemo(() => {
    if (!selectedEndpoint?.details?.responses)
      return { message: "No example available" };
    const success =
      selectedEndpoint.details.responses["200"] ||
      selectedEndpoint.details.responses["201"];

    return success?.examples || success?.schema || { status: "success" };
  }, [selectedEndpoint]);

  return (
    <div className={tw`flex h-screen bg-gray-50 overflow-hidden`}>
      {/* Sidebar */}
      <aside
        className={tw`w-72 bg-white border-r border-gray-200 overflow-y-auto`}
      >
        <div className={tw`p-4`}>
          <h3 className={tw`text-xs font-semibold text-gray-500 mb-2 mt-4`}>
            API REFERENCE
          </h3>
          {Object.entries(groupedMenu).map(([tagName, endpoints]) => (
            <div key={tagName} className={tw`mb-2`}>
              <button
                onClick={() => toggleSection(tagName)}
                className={tw`w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors`}
              >
                <span>{tagName}</span>
                {expandedSections[tagName] ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>

              {expandedSections[tagName] && (
                <div className={tw`ml-3 mt-1 border-l border-gray-100`}>
                  {endpoints.map((item, idx) => {
                    const isActive =
                      selectedEndpoint?.path === item?.path &&
                      selectedEndpoint?.method === item.method;
                    return (
                      <button
                        key={idx}
                        onClick={() => setSelectedEndpoint(item)}
                        className={tw`w-full text-left px-4 py-2 text-xs flex justify-between items-center transition-colors ${
                          isActive
                            ? "text-red-600 font-bold bg-red-50 border-l-2 border-red-600 -ml-[1px]"
                            : "text-gray-500 hover:text-gray-900"
                        }`}
                      >
                        <span className={tw`truncate mr-2`}>
                          {item.details?.summary || item.path}
                        </span>
                        <span
                          className={tw`px-2 py-1 text-[9px] font-bold bg-green-100 text-green-700 rounded flex-shrink-0 uppercase border ${getMethodStyles(
                            item.method,
                          )}`}
                        >
                          {item.method}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </aside>

      {/* Main Content */}
      <div className={tw`flex-1 flex flex-col lg:flex-row overflow-hidden`}>
        {/* Content Area */}
        <main className={tw`flex-1 overflow-y-auto bg-white p-8 lg:p-12`}>
          {selectedEndpoint && (
            <div className={tw`max-w-3xl`}>
              <div className={tw`mb-8`}>
                <h1 className={tw`text-3xl font-bold text-gray-900 mb-4`}>
                  {selectedEndpoint.details.summary}
                </h1>
                <div className={tw`flex items-center gap-2 mb-4`}>
                  <span
                    className={tw`px-2 py-1 text-xs font-bold rounded ${getMethodStyles(
                      selectedEndpoint.method,
                    )}`}
                  >
                    {selectedEndpoint.method}
                  </span>
                  <code
                    className={tw`text-sm text-gray-500 font-mono break-all`}
                  >
                    {selectedEndpoint?.path}
                  </code>
                </div>
                <p className={tw`text-gray-600 leading-relaxed`}>
                  {selectedEndpoint.details.description ||
                    "No description provided."}
                </p>
              </div>

              <h3
                className={tw`text-lg font-bold mb-4 text-gray-800 border-b pb-2`}
              >
                Parameters
              </h3>
              <div className={tw`space-y-4`}>
                {(() => {
                  const operationParams =
                    selectedEndpoint?.details.parameters || [];
                  const pathLevelParams =
                    apiData?.paths[selectedEndpoint?.path]?.parameters || [];
                  const allParameters = [
                    ...operationParams,
                    ...pathLevelParams,
                  ];

                  if (allParameters?.length === 0) {
                    return (
                      <p className={tw`text-sm text-gray-400 italic`}>
                        No parameters required.
                      </p>
                    );
                  }

                  return allParameters.flatMap((param: any, i: number) => {
                    const bodyProps = getBodyProperties(param);

                    // Scenario A: Path, Query, or Header Params (like {id})
                    if (param.in !== "body") {
                      return (
                        <div
                          key={`param-${i}`}
                          className={tw`flex flex-col border-b border-gray-50 pb-6`}
                        >
                          <div className={tw`flex items-center gap-2 mb-2`}>
                            <span
                              className={tw`font-mono font-bold text-blue-600`}
                            >
                              {param.name}
                            </span>
                            <span className={tw`text-xs text-gray-400 italic`}>
                              {param.type || "string"}
                            </span>
                            <span
                              className={tw`text-[10px] uppercase font-semibold text-gray-400`}
                            >
                              in {param.in}
                            </span>
                            {param.required && (
                              <span
                                className={tw`text-[10px] text-red-500 font-bold bg-red-50 px-1`}
                              >
                                REQUIRED
                              </span>
                            )}
                          </div>
                          <p className={tw`text-sm text-gray-500 mt-1`}>
                            {param.description || "No description available."}
                          </p>
                        </div>
                      );
                    }

                    // Scenario B: Body Parameters
                    if (bodyProps) {
                      return Object.entries(bodyProps).map(
                        ([name, details]: [string, any], j) => {
                          const isRequired =
                            param.schema?.required?.includes(name);
                          return (
                            <div
                              key={`body-${i}-${j}`}
                              className={tw`group bg-white p-4 -mx-4 rounded-xl hover:bg-gray-50/50 transition-all`}
                            >
                              <div className={tw`flex items-center gap-2 mb-2`}>
                                <span
                                  className={tw`font-mono font-bold text-orange-600 text-sm`}
                                >
                                  {name}
                                </span>
                                <span
                                  className={tw`text-[10px] text-gray-400 font-medium italic bg-gray-50 px-2 py-0.5 rounded`}
                                >
                                  {details.type || "string"}
                                </span>
                                <span
                                  className={tw`text-[10px] uppercase font-bold text-gray-400`}
                                >
                                  In body
                                </span>
                                {isRequired && (
                                  <span
                                    className={tw`text-[9px] text-red-500 font-bold bg-red-50 px-1`}
                                  >
                                    REQUIRED
                                  </span>
                                )}
                              </div>
                              <p
                                className={tw`text-sm text-gray-500 leading-relaxed`}
                              >
                                {details.description ||
                                  "No description provided."}
                              </p>
                            </div>
                          );
                        },
                      );
                    }
                    return null;
                  });
                })()}
              </div>
            </div>
          )}
        </main>

        {/* Right Side Code Panel */}
        <aside
          className={tw`w-full lg:w-96 xl:w-[450px] bg-white border-l border-gray-200 overflow-y-auto`}
        >
          <div className={tw`p-6`}>
            {/* Language Selection */}
            <div className={tw`mb-6`}>
              <h3
                className={tw`text-xs font-semibold text-gray-500 uppercase mb-3`}
              >
                Language
              </h3>
              <div className={tw`flex flex-wrap gap-2`}>
                {languageOptions.map((lang) => (
                  <button
                    key={lang.name}
                    onClick={() => setActiveLanguage(lang)}
                    className={tw`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                      activeLanguage.name === lang.name
                        ? "bg-gray-900 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            </div>

            <div className={tw`mb-6`}>
              <div className={tw`flex items-center justify-between mb-3`}>
                <h3 className={tw`text-xs font-semibold text-gray-500`}>
                  CREDENTIALS
                </h3>
                <button className={tw`text-gray-400 hover:text-gray-600`}>
                  <Info className={tw`w-4 h-4`} />
                </button>
              </div>

              <div className={tw`flex items-center gap-2`}>
                <span className={tw`text-sm text-gray-600 min-w-[60px]`}>
                  Query
                </span>
                <div className={tw`flex-1 relative`}>
                  <input
                    type="text"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className={tw`w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="key"
                  />
                </div>
                <button className={tw`p-2 text-gray-400 hover:text-gray-600`}>
                  <Lock className={tw`w-4 h-4`} />
                </button>
              </div>
            </div>

            <div className={tw`mb-6`}>
              <h3 className={tw`text-xs font-semibold text-gray-500 mb-3`}>
                URL
              </h3>
              <div className={tw`flex items-start gap-2`}>
                <span className={tw`text-sm text-gray-600 min-w-[80px] mt-2`}>
                  Base URL
                </span>
                <textarea
                  value={`${baseUrl}${selectedEndpoint?.path}`}
                  readOnly
                  className={tw`flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50`}
                />
              </div>
            </div>

            {/* Snippet Header */}
            <div className={tw`flex items-center justify-between mb-4`}>
              <h3
                className={tw`text-[10px] font-bold text-gray-400 uppercase tracking-widest`}
              >
                {activeLanguage.name} / {activeLanguage.client}
              </h3>
              <button
                onClick={handleCopy}
                className={tw`flex items-center gap-2 text-xs font-bold ${
                  copied ? "text-green-600" : "text-blue-600"
                }`}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}{" "}
                {copied ? "COPIED" : "COPY"}
              </button>
            </div>

            {/* Code Display Area */}
            <div
              className={tw`bg-slate-900 rounded-2xl p-6 shadow-2xl mb-8 border border-slate-800`}
            >
              <pre
                className={tw`text-[12px] text-slate-300 font-mono leading-relaxed h-72 overflow-y-auto scrollbar-hide`}
              >
                <code>{generatedCode || "// Snippet loading..."}</code>
              </pre>
            </div>

            <button
              className={tw`w-full py-4 bg-red-500 hover:bg-red-600 text-white font-bold rounded-2xl shadow-xl transition-all active:scale-95 mb-10`}
            >
              Try Request
            </button>

            {/* Response Section */}
            <div className={tw`flex items-center justify-between mb-4`}>
              <h3 className={tw`text-xs font-bold text-gray-900 mb-4`}>
                Response Example
              </h3>
              <button
                onClick={handleCopy}
                className={tw`flex items-center gap-2 text-xs font-bold ${
                  copied ? "text-green-600" : "text-blue-600"
                }`}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}{" "}
                {copied ? "COPIED" : "COPY"}
              </button>
            </div>

            <div
              className={tw`bg-slate-900 rounded-2xl p-6 shadow-2xl mb-8 border border-slate-800`}
            >
              <pre
                className={tw`text-[12px] text-slate-300 font-mono leading-relaxed h-72 overflow-y-auto scrollbar-hide`}
              >
                <code>{JSON.stringify(responseExample, null, 2)} </code>
              </pre>
            </div>

            <button
              className={tw`w-full py-4 bg-red-500 hover:bg-red-600 text-white font-bold rounded-2xl shadow-xl transition-all active:scale-95 mb-10`}
            >
              Try Request
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ApiDocsInterface;
